/** Utilities for extracting field-level errors from API responses (normalises FastAPI and nested shapes) */

import type { ApiError } from './api-client';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * A single normalized field error. Two variants:
 *  - kind: 'row' — VERIFIED path. From the captured `detail.errors[]` envelope.
 *    `rowIndex` comes straight from `entry.index` (zero-based sample row).
 *    `msg` is the free-text pydantic string; it carries NO reliable column.
 *  - kind: 'loc' — SPECULATIVE / unverified on this backend. From a FastAPI
 *    422 `detail: ValidationError[]` with a `loc` path. Kept for forward-compat.
 * Callers MUST switch on `kind` (exhaustive).
 */
export type FieldError =
  | { kind: 'row'; rowIndex: number; msg: string; sample?: Record<string, unknown> }
  | { kind: 'loc'; loc: (string | number)[]; msg: string; type?: string };

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

/** The verified backend envelope: detail is an object with an errors array */
interface CapturedDetailEntry {
  index: number;
  sample: Record<string, unknown>;
  errors: string; // NOTE: this is a string, not an array
}

interface CapturedDetail {
  message: string;
  errors: CapturedDetailEntry[];
}

/** FastAPI 422 ValidationError array entry */
interface FastApiValidationEntry {
  loc: (string | number)[];
  msg: string;
  type?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isApiError(err: unknown): err is ApiError {
  return err !== null && typeof err === 'object' && 'status' in err && 'data' in err;
}

function isCapturedDetail(detail: unknown): detail is CapturedDetail {
  if (detail === null || typeof detail !== 'object' || Array.isArray(detail)) {
    return false;
  }
  const d = detail as Record<string, unknown>;
  // `errors` is a common key — require the full verified shape (string `message`, and every entry
  // a numeric `index` + string `errors`) so other error envelopes aren't misclassified as kind:'row'.
  if (typeof d.message !== 'string' || !Array.isArray(d.errors)) {
    return false;
  }
  return d.errors.every(entry => {
    if (entry === null || typeof entry !== 'object') return false;
    const e = entry as Record<string, unknown>;
    return typeof e.index === 'number' && typeof e.errors === 'string';
  });
}

function isValidationArray(detail: unknown): detail is FastApiValidationEntry[] {
  return (
    Array.isArray(detail) &&
    detail.length > 0 &&
    typeof detail[0] === 'object' &&
    detail[0] !== null &&
    'loc' in detail[0] &&
    'msg' in detail[0]
  );
}

function extractFromDetail(detail: unknown): FieldError[] | null {
  // 1. VERIFIED path: CapturedDetail object with errors array
  if (isCapturedDetail(detail)) {
    return detail.errors.map(entry => ({
      kind: 'row' as const,
      rowIndex: entry.index,
      msg: entry.errors,
      sample: entry.sample,
    }));
  }

  // 2. SPECULATIVE path: FastAPI 422 loc-array
  if (isValidationArray(detail)) {
    return detail.map(entry => ({
      kind: 'loc' as const,
      loc: entry.loc,
      msg: entry.msg,
      type: entry.type,
    }));
  }

  // 3. SPECULATIVE path: plain string detail
  if (typeof detail === 'string') {
    return [{ kind: 'loc', loc: ['detail'], msg: detail }];
  }

  return null;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Extracts field-level errors from an API error response.
 *
 * Returns a discriminated-union array of `FieldError`:
 *   - `kind: 'row'` — VERIFIED: per-sample row error from the captured envelope
 *   - `kind: 'loc'` — SPECULATIVE: FastAPI 422 loc-array or string detail
 *
 * Handles:
 *   - Verified CapturedDetail envelope  (`{ detail: { message, errors: [{ index, sample, errors }] } }`)
 *   - FastAPI 422 validation arrays     (`{ detail: [{ loc, msg }] }`)
 *   - Simple detail strings             (`{ detail: "..." }`)
 *   - Simple message strings            (`{ data: { message: "..." } }`)
 *   - Nested response shapes            (`{ response: { data: { detail } } }`)
 *   - Plain Error objects               (`error.message`)
 *
 * @example
 * ```ts
 * try {
 *   await apiClient.post(url, body);
 * } catch (err) {
 *   const errors = extractFieldErrors(err);
 *   for (const e of errors) {
 *     if (e.kind === 'row') { // row-level error }
 *     else { // loc-level error }
 *   }
 * }
 * ```
 */
export function extractFieldErrors(error: unknown): FieldError[] {
  if (!error || typeof error !== 'object') {
    return [];
  }

  // 1. ApiError from api-client (has .data with the parsed response body)
  if (isApiError(error) && error.data && typeof error.data === 'object') {
    const data = error.data as Record<string, unknown>;

    const fromDetail = extractFromDetail(data.detail);
    if (fromDetail !== null) {
      return fromDetail;
    }

    // Simple message string
    if (typeof data.message === 'string') {
      return [{ kind: 'loc', loc: ['detail'], msg: data.message }];
    }
  }

  // 2. Nested response shape (e.g. Axios-style): { response: { data: { detail } } }
  const maybeNested = error as { response?: { data?: Record<string, unknown> } };
  if (maybeNested.response?.data && typeof maybeNested.response.data === 'object') {
    const nestedData = maybeNested.response.data;

    const fromDetail = extractFromDetail(nestedData.detail);
    if (fromDetail !== null) {
      return fromDetail;
    }
  }

  // 3. Plain Error or unknown object with .message
  if ('message' in error && typeof (error as { message: unknown }).message === 'string') {
    return [{ kind: 'loc', loc: ['detail'], msg: (error as { message: string }).message }];
  }

  return [];
}
